import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';

const getMenuStyle = (triggerEl) => {
  const rect = triggerEl.getBoundingClientRect();
  return {
    position: 'fixed',
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
    zIndex: 1100,
  };
};

const TableRowActionsMenu = ({ items, isOpen, onToggle, onClose, ariaLabel = 'More actions' }) => {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    if (triggerRef.current) {
      setMenuStyle(getMenuStyle(triggerRef.current));
    }
  }, [isOpen, items]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        containerRef.current?.contains(target)
        || dropdownRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleReposition = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, onClose]);

  if (!items || items.length === 0) {
    return null;
  }

  const dropdown = isOpen && menuStyle
    ? createPortal(
      <div
        ref={dropdownRef}
        className="table-actions-menu-dropdown"
        style={menuStyle}
        role="menu"
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={`table-actions-menu-item${item.danger ? ' danger' : ''}`}
            onClick={() => {
              item.onClick();
              onClose();
            }}
          >
            {item.icon && <span className="table-actions-menu-item-icon">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}
      </div>,
      document.body
    )
    : null;

  return (
    <div className="table-actions-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="action-button table-actions-menu-trigger"
        onClick={onToggle}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>
      {dropdown}
    </div>
  );
};

export default TableRowActionsMenu;
